import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import {
    autoinject,
    bind,
    Duration,
    Enums,
    formatDuration,
    inject,
    ModalHandle,
    ServicesAlert,
    SoftwareUpdateAlert,
    Tooltip,
} from 'Mri.Web.Common';
import * as React from 'react';
import { LinkContainer } from 'react-router-bootstrap';
import { ButtonGroup, Dropdown, DropdownItem, DropdownMenu, DropdownToggle } from 'reactstrap';
import { AccountStore } from '../Account/AccountStore';
import { AccountsStore } from '../Accounts/AccountsStore';
import { ContractsStore } from '../Contracts/ContractsStore';
import { FavoriteButton } from '../Core/FavoriteButton';
import { DisplayListViewModel } from '../DisplayFilters/DisplayListViewModel';
import { DisplayViewTypes } from '../DisplayFilters/DisplayViewSelector';
import { ActiveDisplay } from './ActiveDisplay';
import { DisplayScreenSnapshot } from './DisplayScreenSnapshot';
import { DisplaysStore } from './DisplaysStore';
import { DisplayCellWrapper } from './DisplayCellWrapper';
import { BsPencilSquare } from '@react-icons/all-files/bs/BsPencilSquare';
import { geocodeByAddress } from 'react-places-autocomplete';
import {TicketModal} from "../Core/TicketModal";
import {DisplayEventsModal} from "./DisplayEventsModal";
import {ContractModal} from "../Contracts/ContractModal";
import {TicketsModal} from "../Core/TicketsModal";

export interface IDisplayCellProps {
    onFavoritesClick?(display: ActiveDisplay): void;
    readonly display: ActiveDisplay;
    readonly vm: DisplayListViewModel;
    readonly history: any;
    readonly rowNumber?: number;
}

export interface IDisplayCellMeta {
    render(props: IDisplayCellProps): JSX.Element;
    afterHeaderRender?(vm: DisplayListViewModel): JSX.Element;
    id: string | Mri.WebApi.MeasurementId;
    field?: string | null;
    group?: string;
    shortHeaderLabel?: string;
}

export function cellIdToString(id: string | Mri.WebApi.MeasurementId) {
    const cellIdString = typeof id == 'number' ? Enums.toString(Enums.MeasurementId, id) : id;
    return cellIdString;
}


export function getStatusColor(display: ActiveDisplay, hovered: boolean) {
    if (display.displayStatus == Mri.DisplayStatuses.DisplayStatus.NocCouldNotConnect) return "text-warning";

    if (display.filteredEvents && display.filteredEvents.length > 0) {
        switch (display.state) {
            default:
            case 'good':
                return  '';
                break;
            case 'exception':
                return 'text-info';
                break;
            case 'warning':
                return 'text-warning';
                break;
            case 'critical':
                return 'text-danger';
                break;
        }
    }

    return '';
}

@autoinject
@observer
export class SerialNumberCell extends React.Component<IDisplayCellProps> {
    public render() {
        const { display } = this.props;
        return (
            <DisplayCellWrapper
                className="align-middle"
                vm={this.props.vm}
                cellId="Serial Number"
                dropdownOpen={this.dropdownOpen}
            >
                <div className="d-flex align-items-center">
                    <ButtonGroup size={this.buttonSize}>
                        <LinkContainer to={`/displays/${display.serialNumber}`}>
                            <a className={this.buttonClassName} data-selector={`display-serial-number-button`}>
                                {display.serialNumber}
                            </a>
                        </LinkContainer>
                        <Dropdown isOpen={this.dropdownOpen} toggle={this.toggleDropdown}>
                            <DropdownToggle caret color="primary" size={this.buttonSize} className="px-1" />
                            <DropdownMenu>
                                <DropdownItem
                                    tag="a"
                                    href={`/proxy/${display.serialNumber}`}
                                    target={this.accountStore.user?.statusVuNewTab ? '_blank' : ''}
                                    className={this.disabledClass}
                                >
                                    StatusVu&reg;
                                </DropdownItem>

                                <DropdownItem onClick={this.openCreateTicketModal}>Create Ticket</DropdownItem>

                                <DropdownItem onClick={this.openContractModal}>View Contract</DropdownItem>
                                
                                <DropdownItem>
                                    <FavoriteButton display={display} showText onClick={this.props.onFavoritesClick} />
                                </DropdownItem>
                            </DropdownMenu>
                        </Dropdown>
                    </ButtonGroup>
                    

                    <ServicesAlert className="ps-2 my-2" contract={this.contract} display={display} />

                    {this.renderModals && (
                        <>
                            {this.contract && (
                                <ContractModal
                                    contract={this.contract}
                                    display={display}
                                    modalHandle={this.contractModalHandle}
                                    viewState="readOnly"
                                />
                            )}
                            <TicketModal display={display} modalHandle={this.createTicketModalHandle} />
                        </>
                    )}
                </div>
            </DisplayCellWrapper>
        );
    }

    @bind
    @action
    private toggleDropdown() {
        this.dropdownOpen = !this.dropdownOpen;
        this.props.vm.stickyColumnDropdownOpen = !this.props.vm.stickyColumnDropdownOpen;
    }

    @bind
    @action
    private openContractModal(event: React.MouseEvent<HTMLAnchorElement>) {
        event.preventDefault();
        this.renderModals = true;

        this.contractModalHandle.open();
    }

    @bind
    @action
    private openCreateTicketModal(event: React.MouseEvent<HTMLAnchorElement>) {
        event.preventDefault();
        this.renderModals = true;

        this.createTicketModalHandle.open();
    }

    @computed
    private get disabledClass() {
        if (
            !this.props.display ||
            !this.props.display.displayConnectionData ||
            !this.props.display.displayConnectionData.displayBoardIP ||
            !this.props.display.displayConnectionData.nocServer
        ) {
            return 'disabled';
        } else return '';
    }

    @computed
    private get buttonSize() {
        switch (this.props.vm.viewType) {
            default:
            case DisplayViewTypes.TableLarge:
            case DisplayViewTypes.CardView:
                return 'md';
            case DisplayViewTypes.TableSmall:
            case DisplayViewTypes.TableExtraSmall:
                return 'sm';
        }
    }

    @computed
    private get buttonClassName() {
        return `btn btn-primary btn-${this.buttonSize}`;
    }

    @computed
    private get contract() {
        return this.contractsStore.contracts.get(this.props.display.contract);
    }

    @observable
    private renderModals = false;
    @observable
    private dropdownOpen = false;
    private contractModalHandle = new ModalHandle();
    private createTicketModalHandle = new ModalHandle();
    @inject
    private accountStore!: AccountStore;
    @inject
    private contractsStore!: ContractsStore;
}

@autoinject
@observer
export class AddressCell extends React.Component<IDisplayCellProps> {
    public render() {
        const { vm, display } = this.props;

        return (
            <DisplayCellWrapper vm={vm} cellId="Address" className="align-middle">
                {display.fullAddress
                    ? display.fullAddress +
                    (display?.measurements?.find(s => s.id == Mri.WebApi.MeasurementId.GpsLatitude)?.floatValue
                        ? ' (GPS)'
                            : ' (SmartVu)')
                    : '-'}
            </DisplayCellWrapper>
        );
    }

}

@autoinject
@observer
export class SoftwareUpdateStatusCell extends React.Component<IDisplayCellProps> {
    // public render() {
    //     const { display, vm } = this.props;
    //     return (
    //         <DisplayCellWrapper vm={vm} cellId="Update Status" className="align-middle">
    //             <div>
    //                 <SoftwareUpdateAlert updateStatus={this.updateStatus} />
    //
    //                 <span className="px-2 pt-2">
    //                     {this.updateStatus && this.updateStatus.state
    //                         ? Enums.toString(Enums.SoftwareUpdateState, this.updateStatus.state)
    //                         : '-'}
    //                 </span>
    //             </div>
    //         </DisplayCellWrapper>
    //     );
    // }
    //
    // @computed
    // private get updateStatus() {
    //     return this.props.display.displaySoftwareUpdateStatus;
    // }
}

@autoinject
@observer
export class AccountCell extends React.Component<IDisplayCellProps> {
    public render() {
        const { display, vm } = this.props;
        return (
            <DisplayCellWrapper vm={vm} cellId="Account" className="align-middle">
                {this.account?.accountName ?? '-'}
            </DisplayCellWrapper>
        );
    }

    @computed
    private get account() {
        return this.accountsStore.accounts.get(this.props.display.account);
    }

    @inject
    private accountsStore!: AccountsStore;
}

@autoinject
@observer
export class ServiceCell extends React.Component<IDisplayCellProps> {
    public render() {
        const { display, vm } = this.props;
        return (
            <DisplayCellWrapper vm={vm} cellId="Service Level" className="align-middle">
                {this.contract?.serviceLevelType}
            </DisplayCellWrapper>
        );
    }

    @computed
    private get contract() {
        return this.contractsStore.contracts.get(this.props.display.contract);
    }

    @inject
    private contractsStore!: ContractsStore;
}

@autoinject
@observer
export class AdvancedWarrantyAlertsCell extends React.Component<IDisplayCellProps> {
    public render() {
        const { display, vm } = this.props;
        return (
            <DisplayCellWrapper vm={vm} cellId="Advanced Warranty Alerts" className="align-middle">
                {this.contract?.advancedWarrantyAlerts ? 'Included' : 'Not Included'}
            </DisplayCellWrapper>
        );
    }

    @computed
    private get contract() {
        return this.contractsStore.contracts.get(this.props.display.contract);
    }

    @inject
    private contractsStore!: ContractsStore;
}

@autoinject
@observer
export class ContractCell extends React.Component<IDisplayCellProps> {
    public render() {
        const { display, vm } = this.props;
        return (
            <DisplayCellWrapper vm={vm} cellId="Contract" className="align-middle">
                <a className="btn-link" onClick={this.openContractModal}>
                    {this.contract?.contractNumber
                        ? this.contract?.contractNumber
                        : this.contract?.serviceCO
                            ? this.contract?.serviceCO
                            : '-'}
                </a>

                {this.renderModals && this.contract && (
                    <ContractModal
                        contract={this.contract}
                        display={display}
                        modalHandle={this.contractModalHandle}
                        viewState="readOnly"
                    />
                )}
            </DisplayCellWrapper>
        );
    }

    @bind
    @action
    private openContractModal(event: React.MouseEvent<HTMLAnchorElement>) {
        event.preventDefault();
        this.renderModals = true;

        this.contractModalHandle.open();
    }

    @computed
    private get contract() {
        return this.contractsStore.contracts.get(this.props.display.contract);
    }

    @observable
    private renderModals = false;
    private contractModalHandle = new ModalHandle();
    @inject
    private contractsStore!: ContractsStore;
}

@autoinject
@observer
export class ServiceCOCell extends React.Component<IDisplayCellProps> {
    public render() {
        const { display, vm } = this.props;
        return (
            <DisplayCellWrapper vm={vm} cellId="ServiceCO" className="align-middle">
                <a className="btn-link" onClick={this.openServiceCOModal}>
                    {this.contract?.serviceCO || '-'}
                </a>

                {this.renderModals && this.contract && (
                    <ContractModal
                        contract={this.contract}
                        display={display}
                        modalHandle={this.contractModalHandle}
                        viewState="readOnly"
                    />
                )}
            </DisplayCellWrapper>
        );
    }

    @bind
    @action
    private openServiceCOModal(event: React.MouseEvent<HTMLAnchorElement>) {
        event.preventDefault();
        this.renderModals = true;

        this.contractModalHandle.open();
    }

    @computed
    private get contract() {
        return this.contractsStore.contracts.get(this.props.display.contract);
    }

    @observable
    private renderModals = false;
    private contractModalHandle = new ModalHandle();
    @inject
    private contractsStore!: ContractsStore;
}

@observer
export class OpenTicketCountCell extends React.Component<IDisplayCellProps> {
    public render() {
        const { display, history, vm } = this.props;

        const onClick = !display.openTicketCount ? this.openCreateTicketModal : this.openTicketsModal;
        const linkClass = 'btn-link';
        return (
            <DisplayCellWrapper vm={vm} cellId="Tickets">
                <a className={linkClass} onClick={onClick}>
                    {display.openTicketCount}
                </a>

                {this.renderModals && (
                    <>
                        <TicketModal display={display} modalHandle={this.createTicketModalHandle} />
                        <TicketsModal display={display} modalHandle={this.ticketsModalHandle} />
                    </>
                )}
            </DisplayCellWrapper>
        );
    }

    @bind
    @action
    private openCreateTicketModal(event: React.MouseEvent<HTMLAnchorElement>) {
        event.preventDefault();
        this.renderModals = true;

        this.createTicketModalHandle.open();
    }

    @bind
    @action
    private openTicketsModal(event: React.MouseEvent<HTMLAnchorElement>) {
        event.preventDefault();
        this.renderModals = true;

        this.ticketsModalHandle.open();
    }

    @observable
    private renderModals = false;
    private createTicketModalHandle = new ModalHandle();
    private ticketsModalHandle = new ModalHandle();
}

@observer
export class NewDisplayEventCountCell extends React.Component<IDisplayCellProps> {
    public render() {
        const { display, history, vm } = this.props;
        const { displayFilter } = this.props.vm;
        const linkClass = 'btn-link';
        return (
            <DisplayCellWrapper vm={vm} cellId="New Events">
                <a className={linkClass} onClick={this.openDisplayEventsModal}>
                    <span>{display.newDisplayEventCount}</span>
                </a>

                {this.renderModals && (
                    <>
                        <DisplayEventsModal
                            display={display}
                            displayFilter={displayFilter}
                            modalHandle={this.displayEventsModalHandle}
                            history={history}
                            hideAlerts
                        />
                    </>
                )}
            </DisplayCellWrapper>
        );
    }

    @bind
    @action
    private openDisplayEventsModal(event: React.MouseEvent<HTMLAnchorElement>) {
        event.preventDefault();
        this.renderModals = true;

        this.displayEventsModalHandle.open();
    }

    private displayEventsModalHandle = new ModalHandle();
    @observable
    private renderModals = false;
}

@observer
export class NewDisplayAlertCountCell extends React.Component<IDisplayCellProps> {
    // public render() {
    //     const { display, history, vm } = this.props;
    //     const { displayFilter } = this.props.vm;
    //     const linkClass = 'btn-link';
    //     const alertClass = display.displayAlertCount ? 'text-warning' : '';
    //
    //     return (
    //         <DisplayCellWrapper vm={vm} cellId="Alerts">
    //             <a className={linkClass} onClick={this.openDisplayEventsModal}>
    //                 <span className={alertClass}>{display.displayAlertCount}</span>
    //             </a>
    //
    //             {this.renderModals && (
    //                 <>
    //                     <DisplayEventsModal
    //                         display={display}
    //                         displayFilter={displayFilter}
    //                         modalHandle={this.displayEventsModalHandle}
    //                         history={history}
    //                         showOnlyAlerts
    //                     />
    //                 </>
    //             )}
    //         </DisplayCellWrapper>
    //     );
    // }

    @bind
    @action
    private openDisplayEventsModal(event: React.MouseEvent<HTMLAnchorElement>) {
        event.preventDefault();
        this.renderModals = true;

        this.displayEventsModalHandle.open();
    }

    private displayEventsModalHandle = new ModalHandle();
    @observable
    private renderModals = false;
}

@observer
@autoinject
export class ScreenSnapshotCell extends React.Component<IDisplayCellProps> {
    public async componentDidMount() {
        try {
            const { display } = this.props;
            await this.displaysStore.loadLatestScreenSnapshotAsync(display.serialNumber);

            if (!this.screenSnapshots) {
                await this.displaysStore.loadCurrentScreenSnapshotAsync(display.serialNumber);
            }
        } catch (e: any) {
            console.error(e);
        }
    }

    public render() {
        return (
            <DisplayCellWrapper vm={this.props.vm} cellId="PopVu™" style={{ minHeight: '1px' }}>
                <div
                    className="align-items-center justify-content-center h-100"
                    onLoad={this.onCellLoad}
                    style={{ minHeight: this.minimumHeight + 'px', minWidth: this.minimumWidth + 'px' }}
                >
                    {!this.screenSnapshots &&
                        <span>Not Found</span>
                    }
                    {this.screenSnapshots &&
                        this.screenSnapshots.map(s => (
                            <DisplayScreenSnapshot key={s.uri} screenSnapshot={s} loading={this.props.vm.refreshingSnapshots} className="d-inline-block mx-1" />
                        ))}
                </div>
            </DisplayCellWrapper>
        );
    }

    @bind
    @action
    private onCellLoad(event: React.SyntheticEvent<HTMLDivElement>) {
        const element = event.target as HTMLImageElement;

        this.minimumHeight = this.minimumHeight > element.height ? this.minimumHeight : element.height;
        this.minimumWidth = this.minimumWidth > element.width ? this.minimumWidth : element.width;
    }

    @computed
    private get screenSnapshots() {
        const { display } = this.props;
        const snapshots = this.displaysStore.screenSnapshots.get(display.serialNumber);
        return snapshots?.filter(s => s.captureType == Mri.ScreenSnapshots.CaptureType.Snapshot);
    }

    @observable
    private minimumHeight = 0;
    @observable
    private minimumWidth = 0;
    @inject
    private displaysStore!: DisplaysStore;
}

@observer
@autoinject
export class InfiniteTouchCell extends React.Component<IDisplayCellProps> {
    public async componentDidMount() {
        try {
            const { display } = this.props;
            await this.displaysStore.loadLatestScreenSnapshotAsync(display.serialNumber);
        } catch (e: any) {
            console.error(e);
        }
    }

    public render() {
        const { display } = this.props;
        return (
            <DisplayCellWrapper vm={this.props.vm} cellId="InfiniteTouch®" style={{ minHeight: '1px' }}>
                <div
                    className="align-items-center h-100"
                    onLoad={this.onCellLoad}
                    style={{ minHeight: this.minimumHeight + 'px', minWidth: this.minimumWidth + 'px' }}
                >
                    {this.screenSnapshots &&
                        this.screenSnapshots.map(s => (
                            <DisplayScreenSnapshot key={s.uri} screenSnapshot={s} loading={this.props.vm.refreshingSnapshots} className="d-inline-block mx-1" />
                        ))}
                </div>
            </DisplayCellWrapper>
        );
    }

    @bind
    @action
    private onCellLoad(event: React.SyntheticEvent<HTMLDivElement>) {
        const element = event.target as HTMLImageElement;

        this.minimumHeight = this.minimumHeight > element.height ? this.minimumHeight : element.height;
        this.minimumWidth = this.minimumWidth > element.width ? this.minimumWidth : element.width;
    }

    @computed
    private get screenSnapshots() {
        const { display } = this.props;
        const snapshots = this.displaysStore.screenSnapshots.get(display.serialNumber);
        return snapshots?.filter(s => s.captureType == Mri.ScreenSnapshots.CaptureType.TouchHeatmap);
    }

    @observable
    private minimumHeight = 0;
    @observable
    private minimumWidth = 0;
    @inject
    private displaysStore!: DisplaysStore;
}

interface IMeasurementCellProps extends IDisplayCellProps {
    readonly id: Mri.WebApi.MeasurementId;
    readonly duration?: boolean;
    readonly fixedCount?: number;
    readonly fallbackValue?: any;
    readonly ignoreZero?: boolean;
}

@autoinject
@observer
export class MeasurementCell extends React.Component<IMeasurementCellProps> {
    public render() {
        const { fixedCount, fallbackValue, ignoreZero } = this.props;

        if (this.measurement && ignoreZero) {
            if (
                this.measurement.doubleValue == 0 ||
                this.measurement.intValue == 0 ||
                this.measurement.floatValue == 0
            ) {
                return (
                    <DisplayCellWrapper vm={this.props.vm} cellId={this.props.id}>
                        {fallbackValue ?? '-'}
                    </DisplayCellWrapper>
                );
            }
        }

        if (!this.measurement)
            return (
                <DisplayCellWrapper vm={this.props.vm} cellId={this.props.id}>
                    {fallbackValue ?? '-'}
                </DisplayCellWrapper>
            );

        if (this.measurement) {
            const units = !this.accountStore.storage.hideUnits ? this.measurement.units : '';
            if (this.measurement.boolValue != null) {
                return (
                    <DisplayCellWrapper vm={this.props.vm} cellId={this.props.id}>
                        {this.measurement.boolValue ? 'true' : 'false'}
                    </DisplayCellWrapper>
                );
            } else if (this.measurement.doubleValue != null) {
                let value: string | number = this.measurement.doubleValue;
                if (units == '%') {
                    if (value >= 0 && value <= 1) {
                        value = Math.trunc(value * 100) + '%';
                    } else if (value >= 0 && value <= 100) {
                        value = value + '%';
                    }
                } else if (units) {
                    value = fixedCount ? value.toFixed(fixedCount) : value;
                    value = value + ' ' + units;
                } else {
                    value = fixedCount ? value.toFixed(fixedCount) : value;
                    value = value;
                }
                return (
                    <DisplayCellWrapper vm={this.props.vm} cellId={this.props.id}>
                        {value ?? fallbackValue}
                    </DisplayCellWrapper>
                );
            } else if (this.measurement.floatValue != null) {
                let value: string | number = this.measurement.floatValue;
                if (units == '%') {
                    if (value >= 0 && value <= 1) {
                        value = Math.trunc(value * 100) + '%';
                    } else if (value >= 0 && value <= 100) {
                        value = value + '%';
                    }
                } else if (units) {
                    value = fixedCount ? value.toFixed(fixedCount) : value;
                    value = value + ' ' + units;
                } else {
                    value = fixedCount ? value.toFixed(fixedCount) : value;
                    value = value;
                }
                return (
                    <DisplayCellWrapper vm={this.props.vm} cellId={this.props.id}>
                        {value ?? fallbackValue}
                    </DisplayCellWrapper>
                );
            } else if (this.measurement.intValue != null) {
                let value: string | number = this.measurement.intValue;
                if (this.props.duration) {
                    return (
                        <DisplayCellWrapper vm={this.props.vm} cellId={this.props.id}>
                            {formatDuration(new Duration(value))}
                        </DisplayCellWrapper>
                    );
                }
                if (units == '%' && value >= 0 && value <= 100) {
                    value = value + '%';
                } else if (units) {
                    value = value + ' ' + units;
                }
                return (
                    <DisplayCellWrapper vm={this.props.vm} cellId={this.props.id}>
                        {value}
                    </DisplayCellWrapper>
                );
            } else if (this.measurement.stringValue != null) {
                return (
                    <DisplayCellWrapper vm={this.props.vm} cellId={this.props.id}>
                        {this.measurement.stringValue}
                    </DisplayCellWrapper>
                );
            } else if (this.measurement.durationValue != null) {
                return (
                    <DisplayCellWrapper vm={this.props.vm} cellId={this.props.id}>
                        {formatDuration(this.measurement.durationValue)}
                    </DisplayCellWrapper>
                );
            } else {
                return (
                    <DisplayCellWrapper vm={this.props.vm} cellId={this.props.id}>
                        -
                    </DisplayCellWrapper>
                );
            }
        }
    }

    @computed
    private get measurement() {
        const { display, id } = this.props;

        if (!display.measurements) return;

        return display.measurements.find(s => s.id == id);
    }

    @inject
    private accountStore!: AccountStore;
}

@observer
export class MeasurementSourceCell extends React.Component<IMeasurementCellProps> {
    public render() {
        if (!this.measurement || !this.measurement.intValue)
            return (
                <DisplayCellWrapper vm={this.props.vm} cellId={this.props.id}>
                    -
                </DisplayCellWrapper>
            );

        /* eslint-disable no-bitwise */
        const source = this.measurement.intValue & Mri.WebApi.MeasurementId.SENSOR_ID_SOURCE_MASK;
        const id = this.measurement.intValue & Mri.WebApi.MeasurementId.SENSOR_ID_MASK;
        /* eslint-enable no-bitwise */
        let prefix = Enums.MeasurementIdSourcePrefix.get(source) || '';
        prefix = prefix ? ` (${prefix})` : '';
        const title = Enums.BriefMeasurementId.has(id)
            ? Enums.toString(Enums.BriefMeasurementId, id)
            : Enums.toString(Enums.MeasurementId, id);
        let tooltip = Enums.toString(Enums.MeasurementId, id);
        if (source) tooltip = Enums.toString(Enums.MeasurementId, source) + ' ' + tooltip;

        return (
            <DisplayCellWrapper vm={this.props.vm} cellId={this.props.id}>
                <Tooltip message={tooltip}>{title + prefix}</Tooltip>
            </DisplayCellWrapper>
        );
    }

    @computed
    private get measurement() {
        const { display, id } = this.props;

        if (!display.measurements) return;

        return display.measurements.find(s => s.id == id);
    }
}

interface IMeasurementSourceSpanProps extends IDisplayCellProps {
    readonly id: Mri.WebApi.MeasurementId;
    readonly prefixComma?: boolean;
}
@observer
export class MeasurementSourceSpan extends React.Component<IMeasurementSourceSpanProps> {
    public render() {
        if (!this.measurement || !this.measurement.intValue) return '';

        /* eslint-disable no-bitwise */
        const source = this.measurement.intValue & Mri.WebApi.MeasurementId.SENSOR_ID_SOURCE_MASK;
        const id = this.measurement.intValue & Mri.WebApi.MeasurementId.SENSOR_ID_MASK;
        /* eslint-enable no-bitwise */
        let prefix = Enums.MeasurementIdSourcePrefix.get(source) || '';
        prefix = prefix ? ` (${prefix})` : '';
        const title = Enums.BriefMeasurementId.has(id)
            ? Enums.toString(Enums.BriefMeasurementId, id)
            : Enums.toString(Enums.MeasurementId, id);
        let tooltip = Enums.toString(Enums.MeasurementId, id);
        if (source) tooltip = Enums.toString(Enums.MeasurementId, source) + ' ' + tooltip;

        return <span>{this.props.prefixComma ? ', ' + title + prefix : title + prefix}</span>;
    }

    @computed
    private get measurement() {
        const { display, id } = this.props;

        if (!display.measurements) return;

        return display.measurements.find(s => s.id == id);
    }
}

interface IVideoInputOutputCellProps extends IDisplayCellProps {
    readonly id: Mri.WebApi.MeasurementId;
}

@observer
export class VideoInputOutputCell extends React.Component<IVideoInputOutputCellProps> {
    public render() {
        if (!this.measurement)
            return (
                <DisplayCellWrapper vm={this.props.vm} cellId={this.props.id}>
                    -
                </DisplayCellWrapper>
            );

        if (this.measurement.intValue != null) {
            const value: string | number = this.measurement.intValue;

            return (
                <DisplayCellWrapper vm={this.props.vm} cellId={this.props.id}>
                    {Enums.toString(Enums.VideoInputOutputState, value)}
                </DisplayCellWrapper>
            );
        } else {
            return (
                <DisplayCellWrapper vm={this.props.vm} cellId={this.props.id}>
                    -
                </DisplayCellWrapper>
            );
        }
    }

    @computed
    private get measurement() {
        const { display, id } = this.props;

        if (!display.measurements) return;

        return display.measurements.find(s => s.id == id);
    }
}

interface IVideoRXTXCellProps extends IDisplayCellProps {
    readonly id: Mri.WebApi.MeasurementId;
}

@observer
export class VideoRXTXCell extends React.Component<IVideoRXTXCellProps> {
    public render() {
        if (!this.measurement)
            return (
                <DisplayCellWrapper vm={this.props.vm} cellId={this.props.id}>
                    -
                </DisplayCellWrapper>
            );

        if (this.measurement.intValue != null) {
            const value: string | number = this.measurement.intValue;

            return (
                <DisplayCellWrapper vm={this.props.vm} cellId={this.props.id}>
                    {Enums.toString(Enums.RXTXState, value)}
                </DisplayCellWrapper>
            );
        } else {
            return (
                <DisplayCellWrapper vm={this.props.vm} cellId={this.props.id}>
                    -
                </DisplayCellWrapper>
            );
        }
    }

    @computed
    private get measurement() {
        const { display, id } = this.props;

        if (!display.measurements) return;

        return display.measurements.find(s => s.id == id);
    }
}

interface IPowerGovernorSourceCellProps extends IDisplayCellProps {
    readonly id: Mri.WebApi.MeasurementId;
}

@observer
export class PowerGovernorSourceCell extends React.Component<IPowerGovernorSourceCellProps> {
    public render() {
        if (!this.measurement)
            return (
                <DisplayCellWrapper vm={this.props.vm} cellId={this.props.id}>
                    -
                </DisplayCellWrapper>
            );

        if (this.measurement.intValue != null) {
            const value: string | number = this.measurement.intValue;
            return (
                <DisplayCellWrapper vm={this.props.vm} cellId={this.props.id}>
                    {Enums.toString(Enums.PowerGovernorSourceState, value) || '-'}
                </DisplayCellWrapper>
            );
        } else {
            return (
                <DisplayCellWrapper vm={this.props.vm} cellId={this.props.id}>
                    -
                </DisplayCellWrapper>
            );
        }
    }

    @computed
    private get measurement() {
        const { display, id } = this.props;

        if (!display.measurements) return;

        return display.measurements.find(s => s.id == id);
    }
}

interface IChartSourceCellProps extends IDisplayCellProps {
    readonly id: Mri.WebApi.MeasurementId;
}

@observer
export class ChartSourceCell extends React.Component<IChartSourceCellProps> {
    public render() {
        if (!this.measurement)
            return (
                <DisplayCellWrapper vm={this.props.vm} cellId={this.props.id}>
                    -
                </DisplayCellWrapper>
            );

        if (this.measurement.intValue != null) {
            const value: string | number = this.measurement.intValue;
            return (
                <DisplayCellWrapper vm={this.props.vm} cellId={this.props.id}>
                    {Enums.toString(Enums.CoolVuZone1ChartSource, value) || '-'}
                </DisplayCellWrapper>
            );
        } else {
            return (
                <DisplayCellWrapper vm={this.props.vm} cellId={this.props.id}>
                    -
                </DisplayCellWrapper>
            );
        }
    }

    @computed
    private get measurement(): Mri.WebApi.Measurement | undefined {
        const { display, id } = this.props;

        if (!display.measurements) return;

        return display.measurements.find(s => s.id == id);
    }
}

interface IBooleanMeasurementCellProps extends IDisplayCellProps {
    readonly id: Mri.WebApi.MeasurementId;
    readonly trueValue: string;
    readonly falseValue: string;
}

@observer
export class BooleanMeasurementCell extends React.Component<IBooleanMeasurementCellProps> {
    public render() {
        if (this.measurement?.boolValue == null)
            return (
                <DisplayCellWrapper vm={this.props.vm} cellId={this.props.id}>
                    -
                </DisplayCellWrapper>
            );

        return (
            <DisplayCellWrapper vm={this.props.vm} cellId={this.props.id}>
                {this.measurement.boolValue ? this.props.trueValue : this.props.falseValue}
            </DisplayCellWrapper>
        );
    }

    @computed
    private get measurement() {
        const { display, id } = this.props;

        if (!display.measurements) return;

        return display.measurements.find(s => s.id == id);
    }
}

interface IWeatherCodeMeasurementCellProps extends IDisplayCellProps {
    readonly id: Mri.WebApi.MeasurementId;
}

@observer
export class WeatherCodeMeasurementCell extends React.Component<IWeatherCodeMeasurementCellProps> {
    public render() {
        if (this.measurement?.intValue == null)
            return (
                <DisplayCellWrapper vm={this.props.vm} cellId={this.props.id}>
                    -
                </DisplayCellWrapper>
            );
        const description = Enums.toString(Enums.WeatherCodeDescription, this.measurement.intValue);
        return (
            <DisplayCellWrapper vm={this.props.vm} cellId={this.props.id}>
                {description}
            </DisplayCellWrapper>
        );
    }

    @computed
    private get measurement() {
        const { display, id } = this.props;

        if (!display.measurements) return;

        return display.measurements.find(s => s.id == id);
    }
}
